import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { RISK_LEVEL_STYLE } from "@/lib/constants";

const RATINGS = [
  ["integrity_rating", "Integritas Manajemen"],
  ["complexity_rating", "Kompleksitas Entitas"],
  ["independence_rating", "Risiko Independensi"],
  ["going_concern_rating", "Kelangsungan Usaha"],
  ["reporting_issue_rating", "Isu Pelaporan Sebelumnya"],
  ["industry_rating", "Risiko Industri"],
];
const TEXTS = [
  ["client_background", "Latar Belakang Klien"],
  ["scope_limitations", "Pembatasan Ruang Lingkup"],
  ["previous_auditor", "Auditor Pendahulu"],
  ["professional_clearance", "Komunikasi Profesional (Clearance)"],
  ["previous_reporting_issues", "Isu Pelaporan Sebelumnya"],
  ["aml_cft", "Pertimbangan APU/PPT (AML/CFT)"],
  ["legal_regulatory", "Hal Hukum & Regulasi"],
  ["competence_resources", "Kompetensi & Sumber Daya"],
  ["fee_dependency", "Ketergantungan Fee / Konflik Kepentingan"],
];
const STATUS_FLOW = ["Draft", "Prepared", "Reviewed", "Partner Approved"];

export default function Acceptance({ id }) {
  const [d, setD] = useState(null);
  const [saved, setSaved] = useState(null);
  const [reason, setReason] = useState("");
  const load = () => api.get(`/engagements/${id}/acceptance`).then((r) => { setD(r.data); setSaved(r.data); });
  useEffect(() => { load(); }, [id]);
  if (!d) return <div className="text-sm text-muted-foreground">Memuat...</div>;

  const set = (k, v) => setD({ ...d, [k]: v });
  const save = async () => { try { await api.put(`/engagements/${id}/acceptance`, {...d,expected_version:saved?.version || 0}); toast.success("Penilaian disimpan"); await load(); } catch(e) { toast.error(String(e.response?.data?.detail || "Gagal")); } };
  const changeStatus = async (status) => {
    if (JSON.stringify(d)!==JSON.stringify(saved)) return toast.error("Simpan perubahan sebelum tanda tangan");
    if (!reason.trim()) return toast.error("Alasan keputusan wajib diisi");
    try { await api.post(`/engagements/${id}/acceptance/status`, { status,reason,expected_version:saved?.version || 0 }); toast.success(`Status → ${status}`); setReason(""); await load(); }
    catch (e) { toast.error(String(e.response?.data?.detail || "Gagal"), { duration: 6000 }); }
  };
  const score = d.system_risk_score || { value: null, level: "Belum dinilai" };

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-4">
        <div className="bg-white border border-border rounded-sm p-5 space-y-3">
          <div className="font-head font-semibold">Penilaian Naratif</div>
          {TEXTS.map(([k, label]) => (
            <div key={k}><Label>{label}</Label><Textarea rows={2} value={d[k] || ""} onChange={(e) => set(k, e.target.value)} data-testid={`acc-${k}`} /></div>
          ))}
          <div className="border-t border-border pt-3 space-y-2">
            <label className="flex items-center gap-2 text-sm"><Checkbox checked={!!d.management_integrity_complete} onCheckedChange={(v) => set("management_integrity_complete", !!v)} data-testid="acc-integrity-complete" />Penilaian integritas manajemen selesai</label>
            <Textarea rows={2} placeholder="Catatan integritas manajemen" value={d.management_integrity || ""} onChange={(e) => set("management_integrity", e.target.value)} />
            <label className="flex items-center gap-2 text-sm text-red-700"><Checkbox checked={!!d.independence_conflict} onCheckedChange={(v) => set("independence_conflict", !!v)} data-testid="acc-independence-conflict" />Terdapat konflik independensi yang belum terselesaikan</label>
            <Textarea rows={2} placeholder="Catatan independensi" value={d.independence_notes || ""} onChange={(e) => set("independence_notes", e.target.value)} />
          </div>
          <Label>Keputusan perikatan<select className="border w-full p-2" value={d.engagement_decision || ''} onChange={e=>set('engagement_decision',e.target.value)}><option value="">Pilih keputusan</option>{['Accept','Continue','Decline'].map(v=><option key={v} value={v}>{v}</option>)}</select></Label>
          <Button onClick={save} data-testid="acc-save">Simpan Penilaian</Button>
        </div>
      </div>
      <div className="space-y-4">
        <div className="bg-white border border-border rounded-sm p-5 space-y-3">
          <div className="font-head font-semibold">Skor Risiko Tersimpan</div>
          {RATINGS.map(([k, label]) => (
            <div key={k}>
              <Label className="text-xs">{label} ({d[k] ?? "Belum dinilai"})</Label>
              <select aria-label={label} value={d[k] ?? ""} onChange={e=>set(k,e.target.value?Number(e.target.value):null)} className="border w-full p-2" data-testid={`acc-rate-${k}`}><option value="">Belum dinilai</option>{[1,2,3,4,5].map(v=><option key={v} value={v}>{v}</option>)}</select>
            </div>
          ))}
          <div className="flex items-center justify-between border-t border-border pt-3">
            <span className="text-sm text-muted-foreground">Skor Risiko Perikatan</span>
            <span className={`text-xs px-2 py-0.5 border rounded-sm font-semibold ${RISK_LEVEL_STYLE[score.level]}`} data-testid="acc-score">{score.level} ({score.value ?? "—"})</span>
          </div>
          <p className="text-xs text-muted-foreground">Skala deskriptif untuk membantu penilaian; keputusan penerimaan memerlukan pertimbangan dan persetujuan auditor.</p>
          <div><Label className="text-xs">Pertimbangan Auditor</Label><Textarea rows={2} value={d.auditor_judgment || ""} onChange={(e) => set("auditor_judgment", e.target.value)} /></div>
          <div><Label className="text-xs">Rasional Override</Label><Textarea rows={2} value={d.override_rationale || ""} onChange={(e) => set("override_rationale", e.target.value)} /></div>
        </div>
        <div className="bg-white border border-border rounded-sm p-5 space-y-3">
          <div className="font-head font-semibold">Alur Persetujuan</div>
          <div className="flex items-center gap-1 flex-wrap">
            {STATUS_FLOW.map((s) => (
              <span key={s} className={`text-[11px] px-2 py-0.5 border rounded-sm ${d.status === s ? "bg-primary text-primary-foreground" : "bg-muted"}`}>{s}</span>
            ))}
          </div>
          <div className="text-sm">Status saat ini: <span className="font-semibold" data-testid="acc-status">{d.status}</span></div>
          {d.partner_name && <div className="text-xs text-green-700">Disetujui oleh {d.partner_name}</div>}
          <div className="text-xs">Preparer: {d.prepared_by_user_id || 'Pending'} · Reviewer: {d.reviewed_by_user_id || 'Pending'}</div>
          {(d.blockers || []).map((b,i)=><div key={i} className="text-xs text-amber-700">{b}</div>)}
          <Textarea aria-label="Alasan keputusan penerimaan" value={reason} onChange={e=>setReason(e.target.value)} />
          <div className="flex flex-wrap gap-2 pt-1">
            <Button size="sm" variant="outline" onClick={() => changeStatus("Prepared")} disabled={d.status !== "Draft"} data-testid="acc-prepared">Prepared</Button>
            <Button size="sm" variant="outline" onClick={() => changeStatus("Reviewed")} disabled={d.status !== "Prepared"} data-testid="acc-reviewed">Reviewed</Button>
            <Button size="sm" onClick={() => changeStatus("Partner Approved")} disabled={d.status !== "Reviewed"} data-testid="acc-approve">Setujui (Partner)</Button>
            {d.status !== "Draft" && <Button size="sm" variant="outline" onClick={()=>changeStatus("Draft")}>Reopen</Button>}
            <Button size="sm" variant="outline" className="text-destructive" onClick={() => changeStatus("Rejected")} data-testid="acc-reject">Tolak</Button>
          </div>
          <p className="text-[11px] text-muted-foreground">Gerbang: penerimaan tidak dapat disetujui bila konflik independensi belum selesai, integritas manajemen belum lengkap, atau tanpa persetujuan Partner.</p>
        </div>
      </div>
    </div>
  );
}
